import React from 'react';
import { Sparkles } from 'lucide-react';

// Single ideal feed: alternating carousels (our slide-1 designs) and reels (real covers)
// laid out in a 3-col grid, mimicking the @rajshamani checkerboard pattern.
const tiles = [
    { kind: 'carousel', file: '/scl-covers/our-carousels/c4.png', label: 'CAROUSEL' },
    { kind: 'reel', file: '/scl-covers/reels/DXjp3_mBA6Y.jpg', label: 'REEL' },
    { kind: 'carousel', file: '/scl-covers/our-carousels/c5-saudi.png', label: 'CAROUSEL' },

    { kind: 'reel', file: '/scl-covers/reels/DXg1MB9hqgW.jpg', label: 'REEL' },
    { kind: 'carousel', file: '/scl-covers/our-carousels/c7-fraud.png', label: 'CAROUSEL' },
    { kind: 'reel', file: '/scl-covers/reels/DXhPGsOBPOG.jpg', label: 'REEL' },

    { kind: 'carousel', file: '/scl-covers/our-carousels/c8-onboarding.png', label: 'CAROUSEL' },
    { kind: 'reel', file: '/scl-covers/reels/DXcOuFiB2-W.jpg', label: 'REEL' },
    { kind: 'carousel', file: '/scl-covers/our-carousels/c9-crypto.png', label: 'CAROUSEL' },

    { kind: 'reel', file: '/scl-covers/reels/DXb1XzSIGrM.jpg', label: 'REEL' },
    { kind: 'carousel', file: '/scl-covers/our-carousels/c10-ownership.png', label: 'CAROUSEL' },
    { kind: 'reel', file: '/scl-covers/reels/DXYexC2I7Qk.jpg', label: 'REEL' },

    { kind: 'carousel', file: '/scl-covers/our-carousels/c11-asia.png', label: 'CAROUSEL' },
    { kind: 'reel', file: '/scl-covers/reels/DXWrgV-BEeM.jpg', label: 'REEL' },
    { kind: 'carousel', file: '/scl-covers/our-carousels/c12-vip.png', label: 'CAROUSEL' },

    { kind: 'reel', file: '/scl-covers/reels/DXOTwGOIr_Z.jpg', label: 'REEL' },
    { kind: 'carousel', file: '/scl-covers/our-carousels/c3-onboarding.png', label: 'CAROUSEL' },
    { kind: 'reel', file: '/scl-covers/reels/DXQr-DAhFMD.jpg', label: 'REEL' },
];

const Tile = ({ file, label, kind }) => (
    <div className="relative rounded-lg overflow-hidden border border-white/5 bg-black" style={{ aspectRatio: '1/1' }}>
        <img
            src={file}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
        />
        <div className="absolute top-1.5 right-1.5">
            <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full backdrop-blur-md border ${
                kind === 'reel'
                    ? 'bg-emerald-500/30 border-emerald-400/40 text-emerald-100'
                    : 'bg-cyan-500/30 border-cyan-400/40 text-cyan-100'
            }`}>{label}</span>
        </div>
    </div>
);

export const PresentationMockup = () => {
    return (
        <section className="py-20 px-4">
            <div className="container mx-auto max-w-5xl">
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 backdrop-blur-md mb-3">
                        <Sparkles size={14} className="text-amber-300 mr-2" />
                        <span className="text-sm font-medium text-amber-300 tracking-wide uppercase">Proposed Feed</span>
                    </div>
                    <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-3">
                        The Ideal @sourcecodelab Feed
                    </h2>
                    <p className="text-white/50 max-w-2xl mx-auto">
                        Alternating reel covers and carousel slide-1 designs, in the same checkerboard rhythm a feed like <span className="text-white/80">@rajshamani</span> uses. Strong individual hooks on every tile, and a feed that reads as a single visual system rather than 76 disconnected posts.
                    </p>
                </div>

                <div className="bg-black border border-white/10 rounded-2xl p-3 md:p-4">
                    <div className="grid grid-cols-3 gap-1.5">
                        {tiles.map((tile, i) => (
                            <Tile key={i} {...tile} />
                        ))}
                    </div>
                </div>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
                    <div className="text-center">
                        <p className="text-cyan-300 text-xs uppercase tracking-widest font-mono mb-2">Carousels</p>
                        <p className="text-white/70 text-sm leading-relaxed">
                            Slide-1 covers from our existing SCL carousel design system — bold typography, brand colors, scroll-stop friendly.
                        </p>
                    </div>
                    <div className="text-center">
                        <p className="text-emerald-300 text-xs uppercase tracking-widest font-mono mb-2">Reels</p>
                        <p className="text-white/70 text-sm leading-relaxed">
                            Real reel covers from the existing SCL feed. Faces, motion, hook-on-frame — the format the algorithm rewards most.
                        </p>
                    </div>
                    <div className="text-center">
                        <p className="text-amber-300 text-xs uppercase tracking-widest font-mono mb-2">Rhythm</p>
                        <p className="text-white/70 text-sm leading-relaxed">
                            Alternating tiles so the grid never feels monotone. A scroller stops on the carousel, plays the reel, stops on the next carousel.
                        </p>
                    </div>
                </div>

                <div className="mt-12 max-w-3xl mx-auto text-center">
                    <p className="text-white/50 text-sm leading-relaxed">
                        We'll walk through this live on the call — including a concrete plan to map every recent topic to either a reel cover or a carousel slide-1 in this same visual language.
                    </p>
                </div>
            </div>
        </section>
    );
};
